import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { ToastContext } from 'app/context/ToastContext';
import { getStornofondEntries } from 'app/api/agentApi';

const AgentStornofondPanel = ({ agentId }) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState<{ date: string; description: string; value: string } | null>(null);
  const [savingNew, setSavingNew] = useState(false);
  const { showSuccess, showError } = useContext(ToastContext);

  useEffect(() => {
    if (agentId) loadEntries();
  }, [agentId]);

  const loadEntries = () => {
    setLoading(true);
    getStornofondEntries(agentId)
      .then(r => setEntries(r.data))
      .catch(e => showError('Chyba', e.response?.data?.detail))
      .finally(() => setLoading(false));
  };

  const onCellEditComplete = e => {
    const { rowData, field, newValue } = e;
    if (rowData.internal) return;
    if (newValue === rowData[field]) return;

    const payload = {
      id: rowData.id,
      agent: { id: agentId },
      description: field === 'description' ? newValue : rowData.description,
      value: field === 'value' ? newValue : rowData.value,
      forStornofond: true,
      forProvisions: false,
    };

    axios
      .put(`/api/bonus-deductions/${rowData.id}`, payload)
      .then(r => {
        setEntries(prev => prev.map(en => (en.id === rowData.id ? r.data : en)));
        showSuccess('Úspech', 'Záznam bol upravený.');
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        loadEntries();
      });
  };

  const deleteEntry = (id: number) => {
    axios
      .delete(`/api/bonus-deductions/${id}`)
      .then(() => {
        setEntries(prev => prev.filter(en => en.id !== id));
        showSuccess('Úspech', 'Záznam bol vymazaný.');
      })
      .catch(e => showError('Chyba', e.response?.data?.detail));
  };

  const parseSlovakDate = (dateStr: string): string | null => {
    const match = dateStr.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!match) return null;
    const [, d, m, y] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const saveNewRow = () => {
    if (!newRow?.date?.trim() || !newRow?.description?.trim() || !newRow?.value?.trim()) {
      showError('Chyba', 'Dátum, popis a hodnota sú povinné.');
      return;
    }
    const parsedDate = parseSlovakDate(newRow.date);
    if (!parsedDate) {
      showError('Chyba', 'Neplatný formát dátumu. Použite formát 1.1.2000');
      return;
    }
    // Instant requires full ISO-8601 timestamp
    const createdDateInstant = new Date(parsedDate + 'T00:00:00Z').toISOString();
    const parsedValue = parseFloat(newRow.value.replace(',', '.'));
    if (isNaN(parsedValue)) {
      showError('Chyba', 'Neplatná hodnota.');
      return;
    }
    setSavingNew(true);
    axios
      .post('/api/bonus-deductions', {
        agent: { id: agentId },
        createdDate: createdDateInstant,
        description: newRow.description,
        value: parsedValue,
        forStornofond: true,
        forProvisions: false,
      })
      .then(r => {
        setEntries(prev => [r.data, ...prev]);
        setNewRow(null);
        showSuccess('Úspech', 'Záznam bol pridaný.');
      })
      .catch(e => showError('Chyba', e.response?.data?.detail))
      .finally(() => setSavingNew(false));
  };

  const balance = entries.reduce((sum, e) => sum + (Number(e.value) || 0), 0);

  // --- editors ---

  const textEditor = options => {
    if (options.rowData?.internal) return options.value;
    return (
      <InputText
        value={options.value ?? ''}
        onChange={e => options.editorCallback(e.target.value)}
        onKeyDown={e => e.stopPropagation()}
        style={{ width: '100%' }}
      />
    );
  };

  const numberEditor = options => {
    if (options.rowData?.internal) return options.value;
    return (
      <InputNumber
        value={options.value}
        onValueChange={e => options.editorCallback(e.value)}
        locale="sk"
        minFractionDigits={2}
        maxFractionDigits={2}
        onKeyDown={e => e.stopPropagation()}
      />
    );
  };

  // --- body templates ---

  const dateBody = (row: any) => {
    if (!row.createdDate) return '-';
    return new Date(row.createdDate).toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const valueBody = (row: any) => {
    const v = Number(row.value);
    return (
      <span style={{ color: v >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
        {v.toFixed(2).replace('.', ',')} €
      </span>
    );
  };

  const sourceBody = (row: any) => {
    if (row.internal) return <Tag value="Systém" severity="warning" />;
    return <Tag value="Manuálne" severity="info" />;
  };

  const actionsBody = (row: any) => {
    if (row.internal) return null;
    return (
      <Button
        icon="pi pi-trash"
        severity="danger"
        text
        rounded
        tooltip="Vymazať"
        tooltipOptions={{ position: 'left' }}
        onClick={() => deleteEntry(row.id)}
      />
    );
  };

  const rowClassName = (row: any) => ({
    'surface-100': row.internal,
  });

  // --- header / footer ---

  const tableHeader = (
    <div className="flex justify-content-between align-items-center">
      <span className="font-bold">Záznamy stornofondu</span>
      {!newRow && (
        <Button
          icon="pi pi-plus"
          label="Pridať záznam"
          style={{ backgroundColor: '#e31818', border: 'none' }}
          onClick={() => setNewRow({ date: '', description: '', value: '' })}
        />
      )}
    </div>
  );

  const tableFooter = (
    <div className="flex justify-content-end align-items-center gap-2 pt-2">
      <span className="font-bold text-lg">Aktuálny stornofond:</span>
      <span
        className="font-bold text-xl"
        style={{ color: balance >= 0 ? 'green' : 'red' }}
      >
        {balance.toFixed(2).replace('.', ',')} €
      </span>
    </div>
  );

  return (
    <div>
      <DataTable
        value={entries}
        loading={loading}
        editMode="cell"
        rowClassName={rowClassName}
        header={tableHeader}
        footer={tableFooter}
        emptyMessage="Žiadne záznamy stornofondu"
        sortField="createdDate"
        sortOrder={-1}
      >
        <Column field="createdDate" header="Dátum" body={dateBody} style={{ width: '10rem' }} />
        <Column
          field="description"
          header="Popis"
          editor={textEditor}
          onCellEditComplete={onCellEditComplete}
        />
        <Column
          field="value"
          header="Hodnota (€)"
          body={valueBody}
          editor={numberEditor}
          onCellEditComplete={onCellEditComplete}
          style={{ width: '12rem' }}
        />
        <Column header="Zdroj" body={sourceBody} style={{ width: '9rem' }} />
        <Column field="nrOfProvisionZostava" header="Provízny hárok" style={{ width: '12rem' }} />
        <Column header="" body={actionsBody} style={{ width: '4rem' }} />
      </DataTable>

      {newRow && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 150px auto auto',
            gap: '8px',
            marginTop: '12px',
            padding: '12px',
            border: '1px dashed #ccc',
            borderRadius: '6px',
            background: '#fafafa',
            alignItems: 'center',
          }}
        >
          <InputText
            placeholder="Dátum (1.1.2000)"
            value={newRow.date}
            onChange={e => setNewRow(prev => ({ ...prev, date: e.target.value }))}
          />
          <InputText
            placeholder="Popis *"
            value={newRow.description}
            onChange={e => setNewRow(prev => ({ ...prev, description: e.target.value }))}
          />
          <InputText
            placeholder="Hodnota (€)"
            value={newRow.value}
            onChange={e => setNewRow(prev => ({ ...prev, value: e.target.value }))}
          />
          <Button
            icon="pi pi-check"
            label="Uložiť"
            style={{ backgroundColor: '#e31818', border: 'none' }}
            loading={savingNew}
            onClick={saveNewRow}
          />
          <Button
            icon="pi pi-times"
            label="Zrušiť"
            severity="secondary"
            onClick={() => setNewRow(null)}
          />
        </div>
      )}
    </div>
  );
};

export default AgentStornofondPanel;
